import type {ComponentProps} from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  HelpCircle,
  Info,
  XCircle,
  type LucideIcon
} from 'lucide-react';
import {
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {cn} from '@/lib/utils';
import {statusLabel, statusTone, type StatusTone} from '@/lib/status';

type Status = 'none' | 'in hopper' | 'sent to client' | string;

// Each tone has its own shape, so colour is never the only carrier.
const TONE_ICON: Record<StatusTone, LucideIcon> = {
  success: CheckCircle2,
  warning: AlertTriangle,
  delayed: Clock,
  waiting: Clock,
  info: Info,
  destructive: XCircle,
  muted: HelpCircle
};

// Complete reads in the brand ink rather than green, per the rail design; only a failure is loud.
// --warning and --info as ink on a card fail contrast, so those two lean on their shape.
const TONE_ICON_COLOUR: Record<StatusTone, string> = {
  success: 'text-primary-ink',
  warning: 'text-muted-foreground',
  delayed: 'text-muted-foreground',
  waiting: 'text-muted-foreground',
  info: 'text-muted-foreground',
  destructive: 'text-destructive-ink',
  muted: 'text-muted-foreground'
};

// The one status glyph for every list, card, drawer and timeline that marks a state, drawn from the
// tone's icon so it is painted in exactly one place. With a label it is an image for assistive
// tech; without one it is decoration beside the word that already says the status. The remaining
// props are spread so it can be the element behind a TooltipTrigger's `render`.
export function StatusIcon({
  tone,
  label,
  busy,
  className,
  ...props
}: {
  readonly tone: StatusTone;
  readonly label?: string | undefined;
  // Work in progress, drawn as the Spinner in place of the tone's icon (a running job).
  readonly busy?: boolean | undefined;
} & Omit<ComponentProps<'svg'>, 'children'>) {
  const aria = label ? {role: 'img', 'aria-label': label} : {'aria-hidden': true};
  if (busy) {
    return <Spinner className={cn('size-3 shrink-0', className)} {...aria} />;
  }

  const Icon = TONE_ICON[tone];
  return (
    <Icon
      className={cn('size-3.5 shrink-0', TONE_ICON_COLOUR[tone], className)}
      {...aria}
      {...props}
    />
  );
}

// A submission's own status as an icon whose tooltip carries the words - plus the risk, notes and
// author - for the places with no room for the label beside it.
export function SubmissionStatusIcon({
  status,
  risk,
  notes,
  createdBy
}: {
  readonly status: Status;
  readonly risk?: string;
  readonly notes?: string | null;
  readonly createdBy?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={<StatusIcon tone={statusTone(status)} label={statusLabel(status)} />}
      />
      <TooltipContent side="bottom" align="start" className="max-w-xs text-left">
        <p className="font-medium capitalize">{status}</p>
        {risk ? <p>Risk: {risk}</p> : null}
        {notes ? <p className="mt-1">{notes}</p> : null}
        {createdBy ? <p className="mt-1 opacity-70">By {createdBy}</p> : null}
      </TooltipContent>
    </Tooltip>
  );
}
